import type { Problem } from '../api/types';

/**
 * 標準フォームの事前検査。
 *
 * ここはJSON Schemaの実装ではなく、送信前に利用者へ即時フィードバックするための限定サブセット。
 * ajvのような汎用validatorを持ち込むとShellへ`new Function`相当のコード生成が入るため採用していない。
 * 最終判定は常にサーバー（回答は422、設定PATCHは422/409）であり、ここを通っても受理を意味しない。
 */

export interface FieldIssue {
  key: string;
  message: string;
}

type JsonRecord = Record<string, unknown>;

export interface SubsetOptions {
  /**
   * patternも検査する。開始入力フォーム（StartRunPanel）だけが指定する。
   * HumanTaskの回答・設定では従来どおり検査せず、サーバー検証に委ねる。
   */
  pattern?: boolean;
}

/**
 * schemaのサブセットだけを検査する。
 *
 * 対応keyword: type(string/integer/number/boolean)、required、enum、minLength、maxLength、
 * minimum、maximum、additionalProperties:false（未知キーの検出）。
 * options.pattern を指定したときだけ pattern も検査する。
 *
 * 検査しないもの: object/array/nullのtype、nested properties、items、$ref、format、
 * （options.pattern が無いときの）pattern、allOf/anyOf/oneOf、dependentRequired など上記以外のkeyword全て。
 * これらは違反していても無言で通し、サーバー検証に委ねる。
 * 1キーにつき最初に見つかった違反だけを返す（同じ項目へ複数の指摘を重ねて出さない）。
 */
export function validateSubset(
  schema: unknown,
  value: Record<string, unknown>,
  options: SubsetOptions = {},
): FieldIssue[] {
  const root = asRecord(schema);
  if (!root) return [];

  const properties = asRecord(root['properties']) ?? {};
  const issues: FieldIssue[] = [];

  const required = root['required'];
  if (Array.isArray(required)) {
    for (const key of required) {
      if (typeof key === 'string' && value[key] === undefined) {
        issues.push({ key, message: '必須項目です。' });
      }
    }
  }

  for (const [key, entry] of Object.entries(value)) {
    if (entry === undefined) continue;
    const property = asRecord(properties[key]);
    if (!property) {
      // additionalProperties:false のときだけ未知キーを指摘する。未指定なら通す。
      if (root['additionalProperties'] === false) {
        issues.push({ key, message: 'この項目はschemaに定義されていません。' });
      }
      continue;
    }
    const message = firstViolation(property, entry, options);
    if (message !== undefined) issues.push({ key, message });
  }

  return issues;
}

/**
 * problem.errors[]をキーごとの文言へ寄せる。キーを特定できない要素は''に入れる。
 *
 * OpenAPIのerrors要素は additionalProperties:true のままで要素形が未確定のため、
 * `key`（キー名そのもの）とJSON Pointerの`pointer`の2形式だけを読む。
 * 契約が確定したら片方へ寄せる。Codexへ確定を依頼済み。
 */
export function issuesFromProblem(problem: Problem): FieldIssue[] {
  const errors = problem.errors ?? [];
  return errors.map((entry) => ({
    key: issueKey(entry),
    message: issueMessage(entry, problem),
  }));
}

/**
 * widgetの入力値をschemaの型へ寄せる。空文字と空白だけの入力は未入力(undefined)として扱う。
 * 数値にならない文字列はそのまま返し、type違反としてvalidateSubsetに指摘させる。
 * checkboxのbooleanはそのまま通す。selectはoption値（JSON値）を直接扱うのでここを通さない。
 */
export function coerceFieldValue(raw: string | boolean, type: string | undefined): unknown {
  if (typeof raw === 'boolean') return raw;
  if (raw.trim() === '') return undefined;
  if (type === 'number' || type === 'integer') {
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? raw : parsed;
  }
  return raw;
}

/** 問い合わせの手掛かりになるので、どの失敗でもcodeとtraceIdは隠さず添える。 */
export function problemMetaText(problem: Problem): string {
  return `code: ${problem.code} / traceId: ${problem.traceId}`;
}

/** 事前検査とproblemの指摘をまとめて項目へ配るための索引。同じ文言は重ねない。 */
export function groupIssuesByKey(issues: FieldIssue[]): Map<string, string[]> {
  const grouped = new Map<string, string[]>();
  for (const issue of issues) {
    const messages = grouped.get(issue.key);
    if (messages === undefined) grouped.set(issue.key, [issue.message]);
    else if (!messages.includes(issue.message)) messages.push(issue.message);
  }
  return grouped;
}

/** schemaのproperties[key].type。サブセット外のschemaや未宣言のキーではundefined。 */
export function fieldType(schema: unknown, key: string): string | undefined {
  const properties = asRecord(asRecord(schema)?.['properties']);
  const type = asRecord(properties?.[key])?.['type'];
  return typeof type === 'string' ? type : undefined;
}

function firstViolation(
  property: JsonRecord,
  value: unknown,
  options: SubsetOptions,
): string | undefined {
  const typeMessage = typeViolation(property['type'], value);
  // 型が違う値に長さ・範囲の指摘を重ねても直し方が伝わらないので、型違反だけを返す。
  if (typeMessage !== undefined) return typeMessage;

  const allowed = property['enum'];
  if (Array.isArray(allowed) && !allowed.includes(value)) return '選択肢から選んでください。';

  if (typeof value === 'string') {
    const minLength = property['minLength'];
    if (typeof minLength === 'number' && value.length < minLength) {
      return `${minLength}文字以上で入力してください。`;
    }
    const maxLength = property['maxLength'];
    if (typeof maxLength === 'number' && value.length > maxLength) {
      return `${maxLength}文字以内で入力してください。`;
    }
    const pattern = property['pattern'];
    if (
      options.pattern === true &&
      typeof pattern === 'string' &&
      !matchesPattern(pattern, value)
    ) {
      return `形式が正しくありません（pattern: ${pattern}）。`;
    }
  }

  if (typeof value === 'number') {
    const minimum = property['minimum'];
    if (typeof minimum === 'number' && value < minimum) {
      return `${minimum}以上の値を入力してください。`;
    }
    const maximum = property['maximum'];
    if (typeof maximum === 'number' && value > maximum) {
      return `${maximum}以下の値を入力してください。`;
    }
  }

  return undefined;
}

function typeViolation(type: unknown, value: unknown): string | undefined {
  switch (type) {
    case 'string':
      return typeof value === 'string' ? undefined : '文字列で入力してください。';
    case 'integer':
      return typeof value === 'number' && Number.isInteger(value)
        ? undefined
        : '整数で入力してください。';
    case 'number':
      return typeof value === 'number' && Number.isFinite(value)
        ? undefined
        : '数値で入力してください。';
    case 'boolean':
      return typeof value === 'boolean' ? undefined : 'true/falseで指定してください。';
    default:
      // object/array/null/型の配列指定/type省略は検査対象外。
      return undefined;
  }
}

/**
 * JSON Schemaのpatternは暗黙に前後へ固定されない（部分一致）ので、RegExp#testの意味と同じ。
 * JavaScriptとして読めないpatternは画面では判定せず通し、サーバー検証に委ねる。
 */
function matchesPattern(pattern: string, value: string): boolean {
  let regex: RegExp;
  try {
    regex = new RegExp(pattern, 'u');
  } catch {
    return true;
  }
  return regex.test(value);
}

function issueKey(entry: Record<string, unknown>): string {
  const key = entry['key'];
  if (typeof key === 'string' && key !== '') return key;

  const pointer = entry['pointer'];
  if (typeof pointer === 'string') {
    // "/answer/comment" のような参照は末尾セグメントがフォームのキーに対応する。
    const last = pointer.split('/').pop();
    if (last !== undefined && last !== '') return last;
  }
  return '';
}

function issueMessage(entry: Record<string, unknown>, problem: Problem): string {
  const message = entry['message'];
  if (typeof message === 'string' && message !== '') return message;
  return problem.detail;
}

function asRecord(value: unknown): JsonRecord | undefined {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return undefined;
  return value as JsonRecord;
}
